import React from 'react';
import { cn, toDisplayString } from '@/lib/utils';
import type { ColumnDef, FilterConfig } from '@/components/ui/DataTable';

/**
 * Render the appropriate filter input (text or select) for a table column.
 *
 * Extracted from DataTable / TreeDataTable to eliminate duplication.
 */
export function renderFilterInput(
  filter: FilterConfig,
  columnId: string,
  filters: Record<string, string>,
  handleFilterChange: (columnId: string, value: string) => void
): React.ReactNode {
  if (filter.type === 'select') {
    return (
      <select
        value={filters[columnId] || ''}
        onChange={(e) => handleFilterChange(columnId, e.target.value)}
        className={cn(
          'h-9 w-full px-2 py-1.5 text-sm font-normal font-sans leading-normal border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring',
          !(filters[columnId] || '') && 'text-muted-foreground'
        )}
      >
        <option value="">{filter.placeholder || 'All'}</option>
        {filter.options?.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      type="text"
      value={filters[columnId] || ''}
      onChange={(e) => handleFilterChange(columnId, e.target.value)}
      placeholder={filter.placeholder || `Filter...`}
      className="h-9 w-full px-2 py-1.5 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
    />
  );
}

/**
 * Render a cell's content, using a custom renderer if available or
 * falling back to `toDisplayString` (safe for unknown values — avoids S6551).
 */
export function renderCellContent<T>(
  column: ColumnDef<T>,
  value: unknown,
  row: T
): React.ReactNode {
  if (column.cell) return column.cell(value, row);
  return toDisplayString(value);
}
